import React, { useEffect, useMemo, useState, useRef, useCallback } from 'react'
import StatementEditor from './StatementEditor.jsx'
import ValidationFeedback from './ValidationFeedback.jsx'

const VALIDATION_DEBOUNCE_MS = 800
// ONE function for the default: `() => {}` in the parameter list is a new function every render,
// and it is a dependency of the validation-feedback effect, which sets state, so any form
// rendered without the prop and with feedback re-rendered for ever (it hung the test suite).
const NO_RECORD = () => {}

const StatementForm = ({
  onCreate,
  onValidate,
  activeStatement = null,
  onCancelEdit,
  fieldSchema = [],
  eventSamples = [],
  onRecordValidationResult = NO_RECORD,
  isSubmitting,
  isValidating,
  validationFeedback,
}) => {
  const [statementName, setStatementName] = useState('')
  const [statementText, setStatementText] = useState('')
  const [formError, setFormError] = useState(null)
  const [validationState, setValidationState] = useState('idle')
  const [lastValidatedSignature, setLastValidatedSignature] = useState(null)
  const [editorMarkers, setEditorMarkers] = useState([])
  const [isEditMode, setIsEditMode] = useState(false)
  const validationTimeoutRef = useRef(null)

  useEffect(() => {
    if (activeStatement?.id) {
      setStatementName(activeStatement.statementName ?? '')
      setStatementText(activeStatement.statement ?? '')
      setFormError(null)
      setValidationState('prefilled')
      setLastValidatedSignature(null)
      setEditorMarkers([])
      setIsEditMode(true)
    }
  }, [activeStatement])

  useEffect(() => {
    if (!validationFeedback) {
      return
    }

    // Map validation errors and warnings to Monaco markers (works for both success and error status)
    const errorMarkers = Array.isArray(validationFeedback.errors)
      ? validationFeedback.errors
          .filter((error) => error?.lineNumber && error?.columnNumber)
          .map((error) => ({
            startLineNumber: error.lineNumber,
            startColumn: error.columnNumber,
            endLineNumber: error.lineNumber,
            endColumn: error.columnNumber + 1,
            message: error.message || 'Validation error',
            severity: 8 // MarkerSeverity.Error
          }))
      : []

    const warningMarkers = Array.isArray(validationFeedback.warnings)
      ? validationFeedback.warnings
          .filter((warning) => warning?.lineNumber && warning?.columnNumber)
          .map((warning) => ({
            startLineNumber: warning.lineNumber,
            startColumn: warning.columnNumber,
            endLineNumber: warning.lineNumber,
            endColumn: warning.columnNumber + 1,
            message: warning.message || 'Validation warning',
            severity: 4 // MarkerSeverity.Warning
          }))
      : []

    const allMarkers = [...errorMarkers, ...warningMarkers]
    setEditorMarkers(allMarkers)

    if (validationFeedback.status === 'success') {
      const trimmedValue = statementText.trim()
      setValidationState('validated')
      setLastValidatedSignature(trimmedValue)
      setFormError(null)
    } else if (validationFeedback.status === 'error') {
      setValidationState('error')
      setLastValidatedSignature(null)
    }

    onRecordValidationResult({
      status: validationFeedback.status,
      message: validationFeedback.message,
      statementName: statementName.trim(),
      statement: statementText.trim(),
      errors: validationFeedback.errors ?? []
    })
  }, [validationFeedback, statementName, statementText, onRecordValidationResult])

  const resetForm = () => {
    setStatementName('')
    setStatementText('')
    setFormError(null)
    setValidationState('idle')
    setLastValidatedSignature(null)
    setEditorMarkers([])
    setIsEditMode(false)
  }

  const cancelEdit = () => {
    resetForm()
    onCancelEdit?.() // Notify parent to clear activeStatement and deselect table row
  }

  const handleSubmit = async (event) => {
    event.preventDefault()
    setFormError(null)

    if (!statementName.trim() || !statementText.trim()) {
      setFormError('Statement name and statement text are required.')
      return
    }

    const signature = statementText.trim()
    const isValidated = validationState === 'validated' && lastValidatedSignature === signature
    if (!isValidated) {
      setFormError('Validate EPL with Esper before publishing.')
      return
    }

    try {
      const payload = {
        statementName: statementName.trim(),
        statement: statementText.trim(),
        // instanceName and outputKafkaTopic are handled automatically by backend
      }
      await onCreate(payload)
      resetForm()
    } catch (error) {
      setFormError(error.message ?? 'Unable to create statement.')
    }
  }

  const triggerValidation = useCallback(async (text) => {
    if (!onValidate || !text.trim()) {
      return
    }
    setValidationState('pending')
    await onValidate({
      statement: text,
      // instanceName is handled automatically by backend
    })
  }, [onValidate])

  const handleEditorChange = (nextValue) => {
    setStatementText(nextValue)
    setValidationState('dirty')
    setLastValidatedSignature(null)
    setEditorMarkers([])

    // Clear existing timeout
    if (validationTimeoutRef.current) {
      clearTimeout(validationTimeoutRef.current)
    }

    // Only validate if there's text
    if (nextValue.trim()) {
      validationTimeoutRef.current = setTimeout(() => {
        triggerValidation(nextValue)
      }, VALIDATION_DEBOUNCE_MS)
    }
  }

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (validationTimeoutRef.current) {
        clearTimeout(validationTimeoutRef.current)
      }
    }
  }, [])

  const canPublish = useMemo(() => {
    const signature = statementText.trim()
    return validationState === 'validated' && lastValidatedSignature === signature
  }, [statementText, validationState, lastValidatedSignature])

  return (
    <section className={`statement-form ${isEditMode ? 'statement-form--edit-mode' : ''}`}>
      <header className="statement-form__header">
        <h3>{isEditMode ? `Editing: ${statementName}` : 'Create new statement'}</h3>
        {isEditMode && (
          <button
            type="button"
            className="statement-form__cancel-edit"
            onClick={cancelEdit}
            aria-label="Cancel editing and return to create mode"
          >
            × Cancel
          </button>
        )}
      </header>
      <form className="statement-form__body" onSubmit={handleSubmit}>
        {!isEditMode && (
          <div className="statement-form__row">
            <label htmlFor="statement-name">Statement name</label>
            <input
              id="statement-name"
              name="statementName"
              value={statementName}
              onChange={(event) => setStatementName(event.target.value)}
              placeholder="e.g. HighErrorRate"
              required
            />
          </div>
        )}

        <div className="statement-form__row">
          <label htmlFor="statement-text">Statement</label>
          <textarea
            id="statement-text"
            name="statement"
            value={statementText}
            onChange={(event) => handleEditorChange(event.target.value)}
            style={{ position: 'absolute', opacity: 0, height: 0, width: 0, pointerEvents: 'none' }}
            tabIndex={-1}
            aria-hidden="true"
          />
          <StatementEditor
            value={statementText}
            onChange={handleEditorChange}
            validationMarkers={editorMarkers}
            fieldSchema={fieldSchema}
            eventSamples={eventSamples}
            readOnly={isSubmitting}
            height="50vh"
            ariaLabel="Esper statement editor"
          />
        </div>

        <ValidationFeedback
          validationResult={validationFeedback}
          isValidating={isValidating}
        />

        {formError ? (
          <div className="statement-form__feedback statement-form__feedback--error" role="alert">
            {formError}
          </div>
        ) : null}

        <div className="statement-form__actions">
          <div className="statement-form__actions-end">
            {!isEditMode && (
              <button type="button" className="secondary" onClick={resetForm} disabled={isSubmitting}>
                Clear form
              </button>
            )}
            <button type="submit" disabled={isSubmitting || !canPublish}>
              {isSubmitting ? (isEditMode ? 'Updating…' : 'Publishing…') : (isEditMode ? 'Update statement' : 'Publish statement')}
            </button>
          </div>
        </div>
      </form>

    </section>
  )
}

export default StatementForm
